import React from "react";
import { Author } from "../../types/community";
import LoadingSpinner from "../common/LoadingSpinner";
import ModalShell from "../modals/ModalShell";

interface LikersModalProps {
  isOpen: boolean;
  onClose: () => void;
  likers: Author[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  title?: string;
  onUserClick?: (liker: Author) => void;
}

const LikersModal: React.FC<LikersModalProps> = ({
  isOpen,
  onClose,
  likers,
  loading,
  error = null,
  onRetry,
  title = "Likes",
  onUserClick,
}) => {
  if (!isOpen) return null;

  const getInitials = (firstName?: string | null, lastName?: string | null) =>
    `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();

  const getDisplayName = (liker: Author) => {
    const fullName = `${liker.first_name || ""} ${
      liker.last_name || ""
    }`.trim();
    return fullName || liker.email || "Unknown User";
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      layout="column"
    >
      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
        {loading && (
          <div className="flex justify-center py-6">
            <LoadingSpinner bare size="lg" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200">
            <p className="text-sm">{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-3 inline-flex items-center text-sm font-medium text-biafit-primary hover:text-biafit-secondary"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {!loading && !error && likers.length === 0 && (
          <div className="py-6 text-center text-gray-600 dark:text-gray-300">
            <p className="text-sm font-medium">No likes yet</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Be the first to like this.
            </p>
          </div>
        )}

        {!loading && !error && likers.length > 0 && (
          <ul className="space-y-3">
            {likers.map((liker) => {
              const displayName = getDisplayName(liker);
              const initials = getInitials(liker.first_name, liker.last_name);
              return (
                <li
                  key={liker.id}
                  className={`flex items-center gap-3 rounded-md border border-gray-200 p-3 transition-colors dark:border-gray-700 ${
                    onUserClick
                      ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700"
                      : ""
                  }`}
                  onClick={() => onUserClick?.(liker)}
                >
                  {liker.profile_image?.full_path ? (
                    <img
                      src={liker.profile_image.full_path}
                      alt={displayName}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-biafit-primary font-medium text-white">
                      {initials || "U"}
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-navy-700 dark:text-white">
                      {displayName}
                    </span>
                    {liker.email && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {liker.email}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </ModalShell>
  );
};

export default LikersModal;
